import { Injectable } from '@angular/core';
import { AuthService } from '../../auth/auth.service';
import { Permissions } from '../api/models';

export type SparkModeKey = 'standard' | 'academy' | 'isd_iop' | 'parent';

export const SparkMode = {
  Standard: 0,
  Academy: 1,
  IsdIop: 2,
  Parent: 3,
} as const;

export type SparkModeId = (typeof SparkMode)[keyof typeof SparkMode];

export type SparkModeQueryParams = {
  sparkMode?: number | null;
  academyMode?: number | null;
};

@Injectable({
  providedIn: 'root',
})
export class AcademyModeService {
  private readonly storageKey = 'sparkMode';
  private readonly legacyAcademyStorageKey = 'sparkAcademyMode';

  constructor(private authService: AuthService) {}

  syncFromCurrentUrl(): boolean {
    this.syncModeFromCurrentUrl();
    return this.isAcademyModeEnabled();
  }

  syncModeFromCurrentUrl(): SparkModeKey {
    const modeFromUrl = this.getSparkModeFromUrl();
    if (modeFromUrl != null) {
      const allowedMode = this.allowedMode(modeFromUrl);
      this.setSparkMode(allowedMode);
      return allowedMode;
    }

    return this.getModeKey();
  }

  isAcademyModeEnabled(): boolean {
    return this.getModeKey() === 'academy';
  }

  isIsdIopModeEnabled(): boolean {
    return this.getModeKey() === 'isd_iop';
  }

  isParentModeEnabled(): boolean {
    return this.getModeKey() === 'parent';
  }

  isSeparatedModeEnabled(): boolean {
    return this.getModeKey() !== 'standard';
  }

  getCurrentSparkModeId(): SparkModeId {
    switch (this.getModeKey()) {
      case 'academy':
        return SparkMode.Academy;
      case 'isd_iop':
        return SparkMode.IsdIop;
      case 'parent':
        return SparkMode.Parent;
      default:
        return SparkMode.Standard;
    }
  }

  setAcademyMode(isAcademyMode: boolean): void {
    this.setSparkMode(isAcademyMode ? 'academy' : 'standard');
  }

  setSparkMode(mode: SparkModeKey): void {
    sessionStorage.setItem(this.storageKey, this.modeKeyToId(mode).toString());
    sessionStorage.setItem(
      this.legacyAcademyStorageKey,
      mode === 'academy' ? '1' : '0'
    );
  }

  getModeKey(): SparkModeKey {
    const modeFromUrl = this.getSparkModeFromUrl();
    if (modeFromUrl != null) {
      return this.allowedMode(modeFromUrl);
    }

    const storedMode = sessionStorage.getItem(this.storageKey);
    if (storedMode != null) {
      return this.allowedMode(this.modeIdToKey(storedMode));
    }

    return this.allowedMode(
      sessionStorage.getItem(this.legacyAcademyStorageKey) === '1'
        ? 'academy'
        : 'standard'
    );
  }

  getAcademyModeQueryParams(): SparkModeQueryParams {
    return this.getSparkModeQueryParams();
  }

  getSparkModeQueryParams(mode: SparkModeKey = this.getModeKey()): SparkModeQueryParams {
    switch (mode) {
      case 'academy':
        return { sparkMode: SparkMode.Academy, academyMode: 1 };
      case 'isd_iop':
        return { sparkMode: SparkMode.IsdIop, academyMode: null };
      case 'parent':
        return { sparkMode: SparkMode.Parent, academyMode: null };
      default:
        return {};
    }
  }

  getClearedSparkModeQueryParams(): SparkModeQueryParams {
    return { sparkMode: null, academyMode: null };
  }

  appendModeToPath(path: string): string {
    return this.appendModeQuery(path);
  }

  appendModeToApiUrl(url: string): string {
    if (
      this.getModeKey() === 'standard' ||
      url.includes('sparkMode=') ||
      url.includes('academyMode=')
    ) {
      return url;
    }

    return this.appendModeQuery(url);
  }

  getImportButtonLabel(): string {
    switch (this.getModeKey()) {
      case 'academy':
        return 'Get From IAP';
      case 'isd_iop':
        return 'Get From MH Treatment Plan';
      default:
        return 'Get From BSP';
    }
  }

  getImportButtonId(): string {
    switch (this.getModeKey()) {
      case 'academy':
        return 'getFromIAPButton';
      case 'isd_iop':
        return 'getFromMHTreatmentPlanButton';
      default:
        return 'getFromBSPButton';
    }
  }

  getCurrentObjectiveLabel(): string {
    switch (this.getModeKey()) {
      case 'academy':
        return 'IAP Current Objective:';
      case 'isd_iop':
        return 'MH Treatment Plan Objective:';
      default:
        return 'BSP Current Objective:';
    }
  }

  getCurrentObjectivePlaceholder(): string {
    switch (this.getModeKey()) {
      case 'academy':
        return 'Select an IAP Current Objective';
      case 'isd_iop':
        return 'Select an MH Treatment Plan Objective';
      default:
        return 'Select a BSP Current Objective';
    }
  }

  getObjectiveShortLabel(): string {
    switch (this.getModeKey()) {
      case 'academy':
        return 'IAP Objective:';
      case 'isd_iop':
        return 'MH Treatment Plan Objective:';
      default:
        return 'BSP Objective:';
    }
  }

  getModeDisplayName(mode: SparkModeKey = this.getModeKey()): string {
    switch (mode) {
      case 'academy':
        return 'Academy Mode';
      case 'isd_iop':
        return 'ISD & IOP Mode';
      case 'parent':
        return 'Parent Mode';
      default:
        return 'Standard Mode';
    }
  }

  getModeDisplayNameById(modeId: number): string {
    return this.getModeDisplayName(this.modeIdToKey(modeId));
  }

  isCurrentHomeMode(sparkModeId: number | null | undefined): boolean {
    return sparkModeId == null || sparkModeId === this.getCurrentSparkModeId();
  }

  getShareableModeOptions(): { label: string; value: SparkModeId }[] {
    const currentMode = this.getModeKey();
    return (['standard', 'academy', 'isd_iop', 'parent'] as SparkModeKey[])
      .filter((mode) => mode !== currentMode && this.allowedMode(mode) === mode)
      .map((mode) => ({
        label: this.getModeDisplayName(mode),
        value: this.modeKeyToId(mode),
      }));
  }

  private appendModeQuery(url: string): string {
    const modeId = this.getCurrentSparkModeId();
    if (modeId === SparkMode.Standard) {
      return url;
    }

    const separator = url.includes('?') ? '&' : '?';
    let result = `${url}${separator}sparkMode=${modeId}`;
    if (modeId === SparkMode.Academy) {
      result += '&academyMode=1';
    }
    return result;
  }

  private getSparkModeFromUrl(): SparkModeKey | null {
    const params = new URLSearchParams(window.location.search);
    const sparkModeFromUrl = params.get('sparkMode');
    if (sparkModeFromUrl != null) {
      return this.modeIdToKey(sparkModeFromUrl);
    }

    const academyModeFromUrl = params.get('academyMode');
    if (academyModeFromUrl == null) {
      return null;
    }

    const normalizedAcademyMode = academyModeFromUrl.toLowerCase();
    return normalizedAcademyMode === '1' || normalizedAcademyMode === 'true'
      ? 'academy'
      : 'standard';
  }

  private modeIdToKey(value: string | number): SparkModeKey {
    const parsed = typeof value === 'number' ? value : Number(value);
    switch (parsed) {
      case SparkMode.Academy:
        return 'academy';
      case SparkMode.IsdIop:
        return 'isd_iop';
      case SparkMode.Parent:
        return 'parent';
      default:
        return 'standard';
    }
  }

  private allowedMode(mode: SparkModeKey): SparkModeKey {
    if (mode === 'standard') {
      return 'standard';
    }

    // Permissions are not loaded yet; keep the requested mode until they are.
    if (!this.authService.userInfo?.Permissions) {
      return mode;
    }

    if (mode === 'academy' && this.authService.hasPermission(Permissions.SparkApp_AcademyMode)) {
      return 'academy';
    }

    if (mode === 'isd_iop' && this.authService.hasPermission(Permissions.SparkApp_IsdIopMode)) {
      return 'isd_iop';
    }

    if (mode === 'parent' && this.authService.hasPermission(Permissions.SparkApp_ParentMode)) {
      return 'parent';
    }

    return 'standard';
  }

  private modeKeyToId(mode: SparkModeKey): SparkModeId {
    switch (mode) {
      case 'academy':
        return SparkMode.Academy;
      case 'isd_iop':
        return SparkMode.IsdIop;
      case 'parent':
        return SparkMode.Parent;
      default:
        return SparkMode.Standard;
    }
  }
}
